import { memo, useEffect, useState } from 'react';

import { ModelProvider, useDollhouse, useSelection } from '@spazious/dollhouse-engine';
import { DecorationNode, FNode, FNodeTypesEnum, FurnitureNode } from '@spazious/dollhouse-fsetup';
import { useStore } from '@spazious/dollhouse-store';
import { useEventCallback } from '@spazious/react-hooks';

import { Annotation } from '../Annotation/index.js';
import { Decoration } from '../Decoration/index.js';
import { FPolygon } from '../FPolygon/index.js';
import { Furniture } from '../Furniture/index.js';
import { Measurement } from '../Measurement/index.js';

const traverseMap = (node: FNode) => {
  const nodes: React.ReactNode[] = [];

  if ((node as any).visible === false) {
    return nodes;
  }

  /* TODO: This should resolved using a map not a switch */
  switch (node.type) {
    case FNodeTypesEnum.Furniture: {
      const { uuid, url, position, rotation, collisions, positionY } =
        node as unknown as FurnitureNode;

      nodes.push(
        <Furniture
          key={uuid}
          collision={collisions?.length > 0}
          position={[position[0], positionY ?? 0, position[1]]}
          rotation={[0, rotation, 0]}
          url={url}
          userData={{
            node,
          }}
          uuid={uuid}
        />
      );
      break;
    }
    case FNodeTypesEnum.Decoration: {
      const { uuid, url, position, rotation, collisions, positionY } =
        node as unknown as DecorationNode;
      nodes.push(
        <Decoration
          key={uuid}
          collision={collisions?.length > 0}
          position={[position[0], positionY ?? 0, position[1]]}
          rotation={[0, rotation, 0]}
          url={url}
          userData={{
            node,
          }}
          uuid={uuid}
        />
      );

      break;
    }
    case FNodeTypesEnum.FComposition: {
      const { uuid, position, rotation, children } = node;
      nodes.push(
        <group
          key={uuid}
          position={[position[0], 0, position[1]]}
          rotation={[0, rotation, 0]}
          userData={{
            node,
          }}
          uuid={uuid}
        >
          {children.map((child) => traverseMap(child))}
        </group>
      );
      break;
    }
    case FNodeTypesEnum.FGroup: {
      const { uuid, position, rotation, children } = node;
      nodes.push(
        <group
          key={uuid}
          position={[position[0], 0, position[1]]}
          rotation={[0, rotation, 0]}
          userData={{
            node,
          }}
          uuid={uuid}
        >
          {children.map((child) => traverseMap(child))}
        </group>
      );
      break;
    }
    case FNodeTypesEnum.FPolygon: {
      const {
        uuid,
        position = [0, 0],
        rotation = 0,
        visible,
        points = [],
        children,
        selector,
      } = node;
      nodes.push(
        <FPolygon
          key={uuid}
          points={points}
          position={[position[0], 0, position[1]]}
          rotation={[0, rotation, 0]}
          selector={selector!}
          userData={{
            node,
          }}
          uuid={uuid}
          visible={visible}
        >
          {children.map((child) => traverseMap(child))}
        </FPolygon>
      );
      break;
    }
    case FNodeTypesEnum.FArea: {
      const {
        uuid,
        position = [0, 0],
        rotation = 0,
        points = [],
        children,
        selector,
        visible,
      } = node;
      nodes.push(
        <FPolygon
          key={uuid}
          points={points}
          position={[position[0], 0, position[1]]}
          rotation={[0, rotation, 0]}
          selector={selector!}
          userData={{
            node,
          }}
          uuid={uuid}
          visible={visible}
        >
          {children.map((child) => traverseMap(child))}
        </FPolygon>
      );
      break;
    }
    case FNodeTypesEnum.Measurement: {
      const { uuid, points = [], selector } = node;
      nodes.push(
        <Measurement
          key={'measurement-' + selector!}
          points={points}
          selector={selector}
          userData={{
            node,
          }}
          uuid={uuid}
        />
      );
      break;
    }
    case FNodeTypesEnum.Annotation: {
      const { uuid, position, selector, text } = node;
      nodes.push(
        <Annotation
          key={'Annotation' + selector}
          position={position}
          selector={selector!}
          text={text}
          userData={{
            node,
          }}
          uuid={uuid}
        />
      );
      break;
    }

    default:
      break;
  }

  return nodes;
};

export type FSetupProps = {};

export const FSetup: React.FC<FSetupProps> = memo(() => {
  const { selected } = useSelection();
  const invalidate = useDollhouse(({ invalidate }) => invalidate);
  const graph = useStore(({ graph }) => graph);
  const [nodes, setNodes] = useState<React.ReactNode[]>([]);

  const updateNodes = useEventCallback(() => {
    setNodes(graph.children.map((child) => traverseMap(child)));
    invalidate(1000);
  });

  useEffect(() => graph.subscribe(updateNodes), [graph, selected, updateNodes]);

  useEffect(() => {
    updateNodes();
  }, [graph.layers, updateNodes]);

  return <ModelProvider>{nodes}</ModelProvider>;
});
